// A last line with fewer words, or narrower than this share of the width, is an orphan
const ORPHAN_MIN_WORDS = 2
const ORPHAN_MIN_SHARE = 0.25
// Measuring rounds; a line exactly at the guide still fits
const EPSILON = 0.5

// Balanced line breaking for one run of words between fixed breaks
// - Minimises the squared slack of every line but the last: even lines, not greedy-full.
// - An orphaned last line costs more than any slack, so earlier lines break sooner.
// - A word wider than the line gets a line of its own.
// - Returns the gap indices to break at; gap `i` sits between word `i` and `i + 1`.
export const breakLines = (
  words: ReadonlyArray<number>,
  gaps: ReadonlyArray<number>,
  maxWidth: number
): Array<number> => {
  const count = words.length
  if (count < 2) return []

  const orphanPenalty = 4 * maxWidth ** 2
  // Tie-breaker: among equal layouts take the one with fewer lines
  const linePenalty = 1
  const cost = new Array<number>(count + 1).fill(Infinity)
  const lineStart = new Array<number>(count + 1).fill(0)
  cost[0] = 0

  for (let end = 1; end <= count; end++) {
    let width = 0
    for (let start = end - 1; start >= 0; start--) {
      width += words[start] + (start < end - 1 ? gaps[start] : 0)
      const single = start === end - 1
      if (!single && width > maxWidth + EPSILON) break

      let lineCost: number
      if (end < count) {
        lineCost = width > maxWidth ? 0 : (maxWidth - width) ** 2
      } else {
        const orphan =
          start > 0 &&
          (end - start < ORPHAN_MIN_WORDS || width < ORPHAN_MIN_SHARE * maxWidth)
        lineCost = orphan ? orphanPenalty : 0
      }

      const total = cost[start] + lineCost + linePenalty
      if (total < cost[end]) {
        cost[end] = total
        lineStart[end] = start
      }
    }
  }

  const breaks: Array<number> = []
  for (let end = count; end > 0; end = lineStart[end]) {
    if (lineStart[end] > 0) breaks.push(lineStart[end] - 1)
  }
  return breaks.reverse()
}
